import { useEffect } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useIsFetching, useIsMutating } from 'react-query'
import { Box, Button, CircularProgress } from '@mui/material'

import { DefaultElementProps } from 'common/interfaces'
import { mainButtonStyle } from 'common/styles'
import { SizingContainer } from 'components/SizingContainer'
import { useBoolean, useIsOnline, useNext } from 'utils/hook'

import { NextButton as NextButtonProps } from './interface'

const NextButton = ({
  name,
  text = 'Continue',
  variant = 'contained',
  onClick,
}: NextButtonProps & DefaultElementProps): JSX.Element => {
  const isOnline = useIsOnline()

  const fetchingNumber = useIsFetching()
  const mutatingNumber = useIsMutating()

  const [isClicked, setClicked, setUnclicked] = useBoolean(false)

  const { control, trigger } = useFormContext()
  const { field } = useController({ name, control })

  const next = useNext()

  const handleClick = (): void => {
    void trigger().then((isValid) => {
      if (!isValid) {
        return
      }

      onClick?.(
        {},
        {
          onError: (e) => {
            console.log(e.message)
          },
        }
      )

      setClicked()
    })
  }

  const goToNextPage = (): void => {
    setUnclicked()
    next()
  }

  useEffect(() => {
    if (isClicked && fetchingNumber === 0 && mutatingNumber === 0) {
      goToNextPage()
    }

    if (isClicked && !isOnline) {
      goToNextPage()
    }
  }, [fetchingNumber, mutatingNumber, isClicked, isOnline])

  return (
    <SizingContainer className="next-btn-wrap">
      <Box sx={mainButtonStyle.container}>
        <Button
          {...field}
          type="button"
          onClick={handleClick}
          variant={variant}
          sx={{
            ...mainButtonStyle.button,
            position: 'relative',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          disabled={isClicked}
        >
          {isClicked && (
            <CircularProgress
              size={30}
              color="inherit"
              sx={{
                position: 'absolute',
              }}
            />
          )}
          {text}
        </Button>
      </Box>
    </SizingContainer>
  )
}

export default NextButton
